import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip, Switch, FormControlLabel } from '@mui/material';
import { FaInfoCircle } from 'react-icons/fa';

import { organizationApi } from '../../../../../../../../services/api';
import LoadingSpinner from '../../../../../../../common/LoadingSpinner';
import { useAlert } from '../../../../../../../../contexts/AlertContext';
import { ROLE_TYPES, ROLE_TYPE_COLORS } from '../../../../../../../../constants/roleTypes';

const PositionTooltip = ({ hidePositionInfo }) => (
  hidePositionInfo ? null : (
    <Tooltip title="This column is for filtering roles only. The individual's position will not be updated in this dialog.">
      <IconButton 
        size="small"
        sx={{ 
          p: 0.5,
          color: 'inherit',
          '&:hover': { backgroundColor: 'transparent' }
        }}
      >
        <FaInfoCircle size={14} />
      </IconButton>
    </Tooltip>
  )
);

const RolesSelection = ({
  selectedPositionForRoles,
  setSelectedPositionForRoles,
  selectedFunctionalRoles,
  setSelectedFunctionalRoles,
  selectedTechnicalRoles,
  setSelectedTechnicalRoles,
  selectedOrganizationalRoles,
  setSelectedOrganizationalRoles,
  initialPosition,
  showFunctionalRoles = true,
  showTechnicalRoles = true,
  showOrganizationalRoles = false,
  readOnly = false,
  allowEmptyRolesSelection = false,
  hidePositionInfo = false,
  showGrades = false,
  selectedGrade,
  setSelectedGrade,
  allowedOrgUnitTypes = [],
  lockPosition = false,
}) => {
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(true);
  const [availableRoles, setAvailableRoles] = useState({ 
    functional: [], 
    technical: [],
    organizational: []
  });
  const [positionsWithRoles, setPositionsWithRoles] = useState([]);
  const [grades, setGrades] = useState([]);
  const initializedRef = useRef(false);
  const selectedPositionRef = useRef(null);

  useEffect(() => {
    fetchPositionsAndRoles();
  }, []);

  // Use useMemo to prevent infinite re-renders
  const groupedPositions = useMemo(() => {
    return positionsWithRoles.reduce((acc, position) => {
      const orgUnitType = position.orgUnitType?.name || 'Other';
      const orgUnitTypeCode = position.orgUnitType?.code || 'Other';
      
      // Skip this position if allowedOrgUnitTypes is not empty and doesn't include this org unit type
      if (allowedOrgUnitTypes.length > 0 && !allowedOrgUnitTypes.includes(orgUnitTypeCode)) {
        return acc;
      }
      
      if (!acc[orgUnitType]) {
        acc[orgUnitType] = [];
      }
      acc[orgUnitType].push(position);

      return acc;

    }, {});
  }, [positionsWithRoles, allowedOrgUnitTypes]);

  useEffect(() => {
    if (initialPosition && positionsWithRoles.length > 0 && !initializedRef.current) {
      const position = positionsWithRoles.find(p => p.id === initialPosition.id);
      if (position) {
        setSelectedPositionForRoles(position);
        initializedRef.current = true;

        // Scroll to selected position after a short delay to ensure DOM is updated
        setTimeout(() => {
          if (selectedPositionRef.current) {
            selectedPositionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    }

    // Reset initialization flag when initialPosition changes
    return () => {
      if (initialPosition) {
        initializedRef.current = false;
      }
    };
  }, [initialPosition, positionsWithRoles, setSelectedPositionForRoles]);

  // Reset initialization flag when initialPosition changes
  useEffect(() => {
    initializedRef.current = false;
  }, [initialPosition?.id]);

  const fetchPositionsAndRoles = async () => {
    try {
      setIsLoading(true);
      const structure = await organizationApi.getStructure();
      setPositionsWithRoles(structure.positions || []);
      setGrades(structure.grades || []);
      setAvailableRoles({
        functional: structure.roles.filter(role => role.type === ROLE_TYPES.FUNCTIONAL),
        technical: structure.roles.filter(role => role.type === ROLE_TYPES.TECHNICAL),
        organizational: structure.roles.filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
      });
    } catch (error) {
      console.error('Error fetching positions and roles:', error);
      showAlert('Failed to load positions and roles', 'error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePositionForRolesSelect = (position) => {
    if (readOnly || lockPosition) return;

    if (selectedPositionForRoles?.id === position.id) {
      setSelectedPositionForRoles(null);
      // Always clear roles when deselecting a position
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      setSelectedOrganizationalRoles([]);
    } else {
      setSelectedPositionForRoles(position);
      // Clear roles when selecting a new position if allowEmptyRolesSelection is true
      // This ensures we can submit with just a position selected
      if (allowEmptyRolesSelection) {
        setSelectedFunctionalRoles([]);
        setSelectedTechnicalRoles([]);
        setSelectedOrganizationalRoles([]);
      }
    }
  };

  const handleRoleToggle = (role, type) => {
    if (readOnly) return;
    
    if (!selectedPositionForRoles && !allowEmptyRolesSelection) return;
    
    switch(type) {
      case ROLE_TYPES.FUNCTIONAL:
        setSelectedFunctionalRoles(prev => 
          prev.includes(role.id) 
            ? prev.filter(id => id !== role.id)
            : [...prev, role.id]
        );
        break;
      case ROLE_TYPES.TECHNICAL:
        setSelectedTechnicalRoles(prev => 
          prev.includes(role.id) 
            ? prev.filter(id => id !== role.id)
            : [...prev, role.id]
        );
        break;
      case ROLE_TYPES.ORGANIZATIONAL:
        setSelectedOrganizationalRoles(prev => 
          prev.includes(role.id) 
            ? prev.filter(id => id !== role.id)
            : [...prev, role.id]
        );
        break;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
    }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${1 + (showGrades ? 1 : 0) + (showFunctionalRoles ? 1 : 0) + (showTechnicalRoles ? 1 : 0) + (showOrganizationalRoles ? 1 : 0)}, 1fr)`,
        gap: 3,
      }}>
        {/* Grades Column */}
        {showGrades && (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '600px'
          }}>
            <Typography
              variant="h6"
              sx={{
                p: 1.5,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: ROLE_TYPE_COLORS.grade.border,
                borderRadius: 1,
                color: ROLE_TYPE_COLORS.grade.text,
                fontSize: '16px',
                fontWeight: 500,
                textTransform: 'lowercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              Grade
            </Typography>
            <Box sx={{ 
              overflowY: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 1,
              height: '600px',
              opacity: 1
            }}>
              {grades.sort((a, b) => a.order - b.order).map(grade => (
                <Box
                  key={grade.id}
                  onClick={() => !readOnly && setSelectedGrade?.(grade)}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    textAlign: 'center',
                    borderColor: selectedGrade?.id === grade.id ? ROLE_TYPE_COLORS.grade.border : '#e0e0e0',
                    borderWidth: selectedGrade?.id === grade.id ? 2 : 1,
                    borderRadius: 2,
                    cursor: readOnly ? 'default' : 'pointer',
                    backgroundColor: selectedGrade?.id === grade.id ? ROLE_TYPE_COLORS.grade.background : 'white',
                    color: selectedGrade?.id === grade.id ? ROLE_TYPE_COLORS.grade.text : '#666',
                    fontWeight: selectedGrade?.id === grade.id ? 600 : 500,
                    transition: 'all 0.2s ease',
                    '&:hover': readOnly ? {} : {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      borderColor: '#4ade80'
                    }
                  }}
                >
                  {grade.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Positions Column */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          height: '600px'
        }}>
          <Typography
            variant="h6"
            sx={{
              p: 1.5,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: ROLE_TYPE_COLORS.position.border,
              borderRadius: 1,
              color: ROLE_TYPE_COLORS.position.text,
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'lowercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            Position
            <PositionTooltip hidePositionInfo={hidePositionInfo} />
          </Typography>
          <Box sx={{ 
            overflowY: 'scroll',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            pt: 1,
            height: '600px',
            opacity: 1
          }}>
            {Object.entries(groupedPositions).map(([orgUnitType, positions]) => (
              <Box key={orgUnitType} sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    px: 2,
                    py: 1,
                    color: '#64748B',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  {orgUnitType}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {positions.map(position => (
                    <Box
                      key={position.id}
                      ref={selectedPositionForRoles?.id === position.id ? selectedPositionRef : null}
                      onClick={() => handlePositionForRolesSelect(position)}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        textAlign: 'center',
                        borderColor: selectedPositionForRoles?.id === position.id
                          ? ROLE_TYPE_COLORS.position.border
                          : '#e0e0e0',
                        borderWidth: selectedPositionForRoles?.id === position.id ? 2 : 1,
                        borderRadius: 2,
                        cursor: lockPosition ? 'default' : 'pointer',
                        backgroundColor: selectedPositionForRoles?.id === position.id
                          ? ROLE_TYPE_COLORS.position.background
                          : 'white',
                        color: selectedPositionForRoles?.id === position.id
                          ? ROLE_TYPE_COLORS.position.text
                          : '#666',
                        fontWeight: selectedPositionForRoles?.id === position.id ? 600 : 500,
                        opacity: lockPosition && selectedPositionForRoles?.id !== position.id ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        '&:hover': lockPosition ? {} : {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          borderColor: ROLE_TYPE_COLORS.position.border
                        }
                      }}
                    >
                      {position.label}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Functional Roles Column */}
        {showFunctionalRoles && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '600px'
          }}>
            <Typography
              variant="h6"
              sx={{
                p: 1.5,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].border,
                borderRadius: 1,
                color: ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].text,
                fontSize: '16px',
                fontWeight: 500,
                textTransform: 'lowercase'
              }}
            >
              Functional Role
            </Typography>
            <Box sx={{
              overflowY: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 1,
              height: '600px',
              opacity: allowEmptyRolesSelection ? 0.5 : (selectedPositionForRoles ? 1 : 0.5)
            }}>
              {(selectedPositionForRoles
                ? availableRoles.functional.filter(role =>
                    selectedPositionForRoles.roles?.some(r => r.id === role.id && r.type === ROLE_TYPES.FUNCTIONAL)
                  )
                : availableRoles.functional
              ).map(role => (
                <Box
                  key={role.id}
                  onClick={() => selectedPositionForRoles && !readOnly && handleRoleToggle(role, ROLE_TYPES.FUNCTIONAL)}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    textAlign: 'center',
                    borderColor: selectedFunctionalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].border
                      : '#e0e0e0',
                    borderWidth: selectedFunctionalRoles.includes(role.id) ? 2 : 1,
                    borderRadius: 2,
                    cursor: readOnly ? 'default' : (selectedPositionForRoles ? 'pointer' : 'not-allowed'),
                    backgroundColor: selectedFunctionalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].background
                      : 'white',
                    color: selectedFunctionalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].text
                      : '#666',
                    fontWeight: selectedFunctionalRoles.includes(role.id) ? 600 : 500,
                    transition: 'all 0.2s ease',
                    '&:hover': readOnly ? {} : (selectedPositionForRoles ? {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      borderColor: ROLE_TYPE_COLORS[ROLE_TYPES.FUNCTIONAL].border
                    } : {})
                  }}
                >
                  {role.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Organizational Roles Column */}
        {showOrganizationalRoles && (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '600px'
          }}>
            <Typography
              variant="h6"
              sx={{
                p: 1.5,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].border,
                borderRadius: 1,
                color: ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].text,
                fontSize: '16px',
                fontWeight: 500,
                textTransform: 'lowercase'
              }}
            >
              Organizational Role
            </Typography>
            <Box sx={{ 
              overflowY: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 1,
              height: '600px',
              opacity: allowEmptyRolesSelection ? 0.5 : (selectedPositionForRoles ? 1 : 0.5)
            }}>
              {(selectedPositionForRoles 
                ? availableRoles.organizational.filter(role => 
                    selectedPositionForRoles.roles?.some(r => r.id === role.id && r.type === ROLE_TYPES.ORGANIZATIONAL)
                  )
                : availableRoles.organizational
              ).map(role => (
                <Box
                  key={role.id}
                  onClick={() => selectedPositionForRoles && !readOnly && handleRoleToggle(role, ROLE_TYPES.ORGANIZATIONAL)}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    textAlign: 'center',
                    borderColor: selectedOrganizationalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].border
                      : '#e0e0e0',
                    borderWidth: selectedOrganizationalRoles.includes(role.id) ? 2 : 1,
                    borderRadius: 2,
                    cursor: readOnly ? 'default' : (selectedPositionForRoles ? 'pointer' : 'not-allowed'),
                    backgroundColor: selectedOrganizationalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].background
                      : 'white',
                    color: selectedOrganizationalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].text
                      : '#666',
                    fontWeight: selectedOrganizationalRoles.includes(role.id) ? 600 : 500,
                    transition: 'all 0.2s ease',
                    '&:hover': readOnly ? {} : (selectedPositionForRoles ? {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      borderColor: ROLE_TYPE_COLORS[ROLE_TYPES.ORGANIZATIONAL].border
                    } : {})
                  }}
                >
                  {role.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Technical Roles Column */}
        {showTechnicalRoles && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '600px'
          }}>
            <Typography
              variant="h6"
              sx={{
                p: 1.5,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].border,
                borderRadius: 1,
                color: ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].text,
                fontSize: '16px',
                fontWeight: 500,
                textTransform: 'lowercase'
              }}
            >
              Technical Role
            </Typography>
            <Box sx={{
              overflowY: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 1,
              height: '600px',
              opacity: allowEmptyRolesSelection ? 0.5 : (selectedPositionForRoles ? 1 : 0.5)
            }}>
              {(selectedPositionForRoles
                ? availableRoles.technical.filter(role =>
                    selectedPositionForRoles.roles?.some(r => r.id === role.id && r.type === ROLE_TYPES.TECHNICAL)
                  )
                : availableRoles.technical
              ).map(role => (
                <Box
                  key={role.id}
                  onClick={() => selectedPositionForRoles && !readOnly && handleRoleToggle(role, ROLE_TYPES.TECHNICAL)}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    textAlign: 'center',
                    borderColor: selectedTechnicalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].border
                      : '#e0e0e0',
                    borderWidth: selectedTechnicalRoles.includes(role.id) ? 2 : 1,
                    borderRadius: 2,
                    cursor: readOnly ? 'default' : (selectedPositionForRoles ? 'pointer' : 'not-allowed'),
                    backgroundColor: selectedTechnicalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].background
                      : 'white',
                    color: selectedTechnicalRoles.includes(role.id)
                      ? ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].text
                      : '#666',
                    fontWeight: selectedTechnicalRoles.includes(role.id) ? 600 : 500,
                    transition: 'all 0.2s ease',
                    '&:hover': readOnly ? {} : (selectedPositionForRoles ? {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      borderColor: ROLE_TYPE_COLORS[ROLE_TYPES.TECHNICAL].border
                    } : {})
                  }}
                >
                  {role.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RolesSelection; 